import { API_BASE, MOCK_ENABLED } from "./config";
import { ApiError } from "./errors";

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export type Query = Record<string, string | number | boolean | undefined | null>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
}

/** A transport turns (method, path, options) into a parsed JSON body or throws ApiError. */
export type Transport = <T>(method: Method, path: string, opts?: RequestOptions) => Promise<T>;

export function buildQuery(query?: Query): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

/** Real HTTP transport: same-origin fetch with the session cookie. */
export const fetchTransport: Transport = async <T>(
  method: Method,
  path: string,
  opts: RequestOptions = {},
): Promise<T> => {
  const headers: Record<string, string> = { Accept: "application/json" };
  let body: string | undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${buildQuery(opts.query)}`, {
      method,
      headers,
      body,
      credentials: "include",
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    throw new ApiError(0, "network_error", "Cannot reach the API server");
  }
  if (res.status === 204 || res.status === 202) {
    return (await safeJson(res)) as T;
  }
  const data = await safeJson(res);
  if (!res.ok) throw ApiError.fromBody(res.status, data);
  return data as T;
};

async function safeJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

let active: Transport = fetchTransport;

/** Swap the transport (mock mode, tests). */
export function setTransport(t: Transport): void {
  active = t;
}

export const request: Transport = (method, path, opts) => active(method, path, opts);

/** Lazily installs the mock transport when VITE_MOCK=1. Call once before rendering. */
export async function initTransport(): Promise<void> {
  if (!MOCK_ENABLED) return;
  const { mockTransport } = await import("./mock/server");
  setTransport(mockTransport);
}
